import React, { useState, useRef } from "react";
import { Info, ChevronDown, Play, Square, Volume2, Copy, Check, Upload, Loader2 } from "lucide-react";
import ConversionSection from "./ConversionSection";
import FeatureCardsWelcomeSection from "../FeatureCardsWelcomeSection";

// ─── Constants ────────────────────────────────────────────────────────────────
const LANGUAGES = [
  "English", "Yoruba", "Igbo", "Hausa", "Twi", "Swahili", "Amharic", "French",
];

const VOICES = [
  { id: "male-1", label: "Male voice 1", description: "Deep, authoritative" },
  { id: "female-1", label: "Female voice 1", description: "Bright, narrator" },
];

const WAVEFORM_BARS = [
  18, 32, 24, 40, 28, 52, 36, 20, 44, 30, 56, 38, 22, 48, 34, 26, 50, 32, 42, 24,
  54, 28, 46, 38, 20, 44, 30, 52, 36, 22, 48, 34, 26, 50, 32, 42, 24, 54, 28, 46,
  36, 20, 44, 30, 52, 36, 22, 48, 34, 26, 50, 32, 16, 14, 20, 30, 18, 22, 26, 20,
  30, 14, 18, 24, 28, 20, 16, 22, 18, 30, 14, 24, 20, 16, 28, 22, 18, 30, 14, 24,
];

// ─── Shared Components ────────────────────────────────────────────────────────
const LanguageDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 border border-gray-200 rounded-xl text-sm text-gray-800 font-medium bg-white hover:border-gray-300 transition-colors"
      >
        <span>{value}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-full mt-1 left-0 right-0 z-20 bg-white border border-gray-200 rounded-xl shadow-lg py-1 max-h-48 overflow-y-auto">
          {LANGUAGES.map((lang) => (
            <button
              key={lang}
              onClick={() => { onChange(lang); setOpen(false); }}
              className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${value === lang
                ? "bg-[#F0FAF6] text-[#2D7A5F] font-semibold"
                : "text-gray-700 hover:bg-gray-50"
                }`}
            >
              {lang}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const VoiceOption = ({ voice, selected, onSelect }) => (
  <button
    onClick={() => onSelect(voice.id)}
    className={`flex-1 text-left px-4 py-3 rounded-xl border-2 transition-all duration-200 ${selected
      ? "border-[#2D7A5F] bg-white"
      : "border-gray-200 bg-white hover:border-gray-300"
      }`}
  >
    <p className="text-sm font-semibold text-gray-900">{voice.label}</p>
    <p className="text-xs text-gray-500 mt-0.5">{voice.description}</p>
  </button>
);

const UploadZone = ({ onFile, file, onClear }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) onFile(f);
  };

  if (file) {
    return (
      <div className="rounded-2xl bg-[#EEF5F3] border border-[#C8E0D8] p-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-white border border-[#C8E0D8] flex items-center justify-center flex-shrink-0">
            <Upload className="w-4 h-4 text-[#2D7A5F]" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-800 truncate max-w-[200px]">{file.name}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {(file.size / (1024 * 1024)).toFixed(2)} MB · {file.type || "audio"}
            </p>
          </div>
        </div>
        <button
          onClick={onClear}
          className="w-6 h-6 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
        >
          <span className="text-sm leading-none">×</span>
        </button>
      </div>
    );
  }

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
      className={`rounded-2xl p-10 text-center cursor-pointer transition-all duration-200 ${dragging
        ? "border-2 border-dashed border-[#2D7A5F] bg-[#F0FAF6]"
        : "border-2 border-dashed border-gray-200 bg-[#EEF5F3] hover:border-[#B2D8CC] hover:bg-[#E6F2EF]"
        }`}
    >
      <input
        ref={inputRef}
        type="file"
        accept="audio/*"
        className="hidden"
        onChange={(e) => e.target.files[0] && onFile(e.target.files[0])}
      />
      <div className="flex flex-col items-center gap-2">
        <div className="w-10 h-10 rounded-full bg-[#2D7A5F]/10 flex items-center justify-center mb-1">
          <Upload className="w-5 h-5 text-[#2D7A5F]" />
        </div>
        <p className="text-sm font-semibold text-gray-800">Drop audio file or click to upload</p>
        <p className="text-xs text-gray-400">MP3 or WAV · up to 25 MB</p>
      </div>
    </div>
  );
};

const Waveform = ({ isPlaying, progress }) => (
  <div className="flex items-end gap-[2px] h-14 w-full">
    {WAVEFORM_BARS.map((h, i) => {
      const filled = i / WAVEFORM_BARS.length < progress;
      return (
        <div
          key={i}
          className={`flex-1 rounded-sm transition-all ${filled ? "bg-white" : "bg-white/30"}`}
          style={{
            height: `${h}px`,
            ...(isPlaying && filled
              ? {
                animationName: "wavePulse",
                animationDuration: `${0.3 + (i % 5) * 0.1}s`,
                animationIterationCount: "infinite",
                animationDirection: "alternate",
                animationTimingFunction: "ease-in-out",
              }
              : {}),
          }}
        />
      );
    })}
    <style>{`
      @keyframes wavePulse {
        from { transform: scaleY(0.6); }
        to   { transform: scaleY(1); }
      }
    `}</style>
  </div>
);

const AudioPlayer = ({ title, onPlayPause, onStop, isPlaying, progress, formatTime }) => (
  <div className="bg-[#2B6651] rounded-2xl p-5">
    <p className="text-xs font-semibold text-[#7EC8A8] uppercase tracking-widest mb-1">
      AUDIO OUTPUT
    </p>
    <p className="text-white text-sm font-medium mb-4">{title}</p>
    <Waveform isPlaying={isPlaying} progress={progress} />
    <div className="flex items-center gap-3 mt-4">
      <button
        onClick={onPlayPause}
        className="w-9 h-9 rounded-full bg-white flex items-center justify-center hover:bg-gray-100 transition-colors flex-shrink-0"
      >
        {isPlaying
          ? <div className="flex gap-[3px]"><div className="w-[3px] h-4 bg-[#2B6651] rounded-sm" /><div className="w-[3px] h-4 bg-[#2B6651] rounded-sm" /></div>
          : <Play className="w-4 h-4 text-[#2B6651] ml-0.5" />
        }
      </button>
      <button
        onClick={onStop}
        className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors flex-shrink-0"
      >
        <Square className="w-3.5 h-3.5 text-white fill-white" />
      </button>
      <button className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors flex-shrink-0" />
      <div className="flex-1 flex items-center gap-3">
        <div className="flex-1 h-[3px] bg-white/30 rounded-full overflow-hidden">
          <div className="h-full bg-white rounded-full transition-all duration-100" style={{ width: `${progress * 100}%` }} />
        </div>
      </div>
      <span className="text-white/70 text-xs font-mono whitespace-nowrap">
        {formatTime(progress)} / 0:12
      </span>
      <div className="w-px h-4 bg-white/30" />
      <Volume2 className="w-4 h-4 text-white/70 flex-shrink-0" />
      <div className="w-16 h-[3px] bg-white/30 rounded-full overflow-hidden">
        <div className="h-full bg-white rounded-full" style={{ width: "70%" }} />
      </div>
    </div>
  </div>
);

const CodeBlockSection = ({ curl, python, nodejs }) => {
  const [activeLang, setActiveLang] = useState("CURL");
  const [copied, setCopied] = useState(false);

  const getCode = () => {
    if (activeLang === "PYTHON") return python;
    if (activeLang === "NODE.JS") return nodejs;
    return curl;
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(getCode());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden mt-6 shadow-sm">
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gray-50/50">
        <div className="flex gap-6">
          {["CURL", "PYTHON", "NODE.JS"].map((lang) => (
            <button
              key={lang}
              onClick={() => setActiveLang(lang)}
              className={`text-xs font-bold tracking-wider pb-1 transition-all ${activeLang === lang
                ? "text-gray-900 border-b-2 border-gray-900"
                : "text-gray-400 hover:text-gray-600"
                }`}
            >
              {lang}
            </button>
          ))}
        </div>
        <button
          onClick={handleCopy}
          className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-500 hover:text-gray-700 transition-all flex items-center justify-center"
        >
          {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>
      <div className="p-5 overflow-x-auto bg-white">
        <pre className="text-xs text-gray-600 font-mono leading-relaxed whitespace-pre">
          {getCode()}
        </pre>
      </div>
    </div>
  );
};

// ─── Tab Components ───────────────────────────────────────────────────────────

const TextToSpeechTab = () => {
  const [language, setLanguage] = useState("Yoruba");
  const [text, setText] = useState("Ìforúkọsílẹ̀ rẹ̀ máa parí ní ọjọ́ mẹta; jọwọ tún ṣe láti tesiwaju iwọlé.");
  const [selectedVoice, setSelectedVoice] = useState("male-1");
  const [loading, setLoading] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const progressRef = useRef(null);

  const handleGenerate = () => {
    if (!text.trim() || loading) return;
    setLoading(true);
    setAudioReady(false);
    setProgress(0);
    setIsPlaying(false);
    setTimeout(() => {
      setLoading(false);
      setAudioReady(true);
    }, 1800);
  };

  const handlePlayPause = () => {
    if (!isPlaying) {
      setIsPlaying(true);
      let p = progress;
      progressRef.current = setInterval(() => {
        p += 0.01;
        if (p >= 1) {
          p = 1;
          clearInterval(progressRef.current);
          setIsPlaying(false);
        }
        setProgress(p);
      }, 120);
    } else {
      setIsPlaying(false);
      clearInterval(progressRef.current);
    }
  };

  const handleStop = () => {
    setIsPlaying(false);
    setProgress(0);
    clearInterval(progressRef.current);
  };

  const formatTime = (frac) => {
    const total = 12;
    const elapsed = Math.round(frac * total);
    return `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, "0")}`;
  };

  const curlCode = `curl -X POST https://api.nkenne.ai/v1/speechtts \\
  -H "Authorization: Bearer NKENNE_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "text": "Ìforúkọsílẹ̀ rẹ̀ máa parí ní ọjọ́ mẹta; jọwọ tún ṣe láti tesiwaju iwọlé.",
    "language": "yoruba",
    "voice": "male voice",
    "format": "mp3"
  }' --output speech.mp3`;

  const pythonCode = `import requests

url = "https://api.nkenne.ai/v1/speechtts"
headers = {
    "Authorization": "Bearer NKENNE_API_KEY",
    "Content-Type": "application/json"
}
data = {
    "text": "Ìforúkọsílẹ̀ rẹ̀ máa parí ní ọjọ́ mẹta; jọwọ tún ṣe láti tesiwaju iwọlé.",
    "language": "yoruba",
    "voice": "male voice",
    "format": "mp3"
}

response = requests.post(url, headers=headers, json=data)
with open("speech.mp3", "wb") as f:
    f.write(response.content)`;

  const nodejsCode = `const fs = require('fs');
const fetch = require('node-fetch');

async function textToSpeech() {
  const response = await fetch('https://api.nkenne.ai/v1/speechtts', {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer NKENNE_API_KEY',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      text: "Ìforúkọsílẹ̀ rẹ̀ máa parí ní ọjọ́ mẹta; jọwọ tún ṣe láti tesiwaju iwọlé.",
      language: "yoruba",
      voice: "male voice",
      format: "mp3"
    })
  });
  const buffer = await response.buffer();
  fs.writeFileSync('speech.mp3', buffer);
}

textToSpeech();`;

  const jsonResponse = `{
    "id": "tts_01HZY28K1A",
    "format": "mp3",
    "duration": "6.4",
    "voice": "yo-male",
    "language": "yo",
    "tokens": 18,
    "url": "https://cdn.nkenne.ai/.../tts_01HZY28K1A.mp3"
  }`;

  return (
    <div className="space-y-6">
      <div className="flex gap-6 items-start">
        {/* Left: Input Text Area */}
        <div className="w-1/2 bg-white border border-gray-200 rounded-2xl p-5 flex flex-col min-h-[300px] justify-between shadow-sm">
          <div className="space-y-4">
            <LanguageDropdown value={language} onChange={setLanguage} />
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="w-full min-h-[140px] resize-none outline-none text-[15px] text-gray-800 leading-relaxed placeholder-gray-400"
            />
          </div>
          <div className="flex justify-end pt-4 mt-2 border-t border-gray-100">
            <button
              onClick={handleGenerate}
              disabled={!text.trim() || loading}
              className="flex items-center gap-2 bg-[#1e6c56] hover:bg-[#175443] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Generate speech
            </button>
          </div>
        </div>

        {/* Right: Target Language + Voice Selector */}
        <div className="w-1/2 bg-white border border-gray-200 rounded-2xl p-6 space-y-6 shadow-sm">
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
              TARGET LANGUAGE
            </p>
            <LanguageDropdown value="English" onChange={() => { }} />
          </div>

          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
              VOICE
            </p>
            <div className="flex gap-3">
              {VOICES.map((voice) => (
                <VoiceOption
                  key={voice.id}
                  voice={voice}
                  selected={selectedVoice === voice.id}
                  onSelect={setSelectedVoice}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Row 3: Audio Output & Sample Response */}
      <div className="flex gap-6 items-start">
        <div className="w-1/2">
          {audioReady ? (
            <AudioPlayer
              title="playground.mp3"
              isPlaying={isPlaying}
              progress={progress}
              onPlayPause={handlePlayPause}
              onStop={handleStop}
              formatTime={formatTime}
            />
          ) : (
            <div className="bg-[#2B6651] rounded-2xl p-6 flex flex-col justify-between min-h-[150px]">
              <div>
                <p className="text-xs font-semibold text-[#7EC8A8] uppercase tracking-widest mb-1">
                  AUDIO OUTPUT
                </p>
                <p className="text-white text-sm font-medium">playground.mp3</p>
              </div>
              <p className="text-white/50 text-sm mt-4">Generate speech to preview audio output</p>
            </div>
          )}
        </div>

        <div className="w-1/2 bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
          <p className="text-sm font-semibold text-gray-800 mb-3 border-b border-gray-100 pb-2">
            Sample response
          </p>
          <pre className="text-xs text-gray-600 font-mono bg-gray-50/50 p-4 rounded-xl overflow-x-auto">
            {jsonResponse}
          </pre>
        </div>
      </div>

      {/* Row 4: API Code Block */}
      <CodeBlockSection curl={curlCode} python={pythonCode} nodejs={nodejsCode} />
    </div>
  );
};

const SpeechToTextTab = () => {
  const [language, setLanguage] = useState("English");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [transcript, setTranscript] = useState("");

  const handleTranscribe = () => {
    if (!uploadedFile || loading) return;
    setLoading(true);
    setTranscript("");
    setTimeout(() => {
      setLoading(false);
      setTranscript("Welcome to NKENNEai Speech Studio. Your audio has been transcribed successfully.");
    }, 1800);
  };

  const curlCode = `curl -X POST https://api.nkenne.ai/v1/speechstt \\
  -H "Authorization: Bearer NKENNE_API_KEY" \\
  -F "file=@audio.mp3" \\
  -F "language=en"`;

  const pythonCode = `import requests

url = "https://api.nkenne.ai/v1/speechstt"
headers = { "Authorization": "Bearer NKENNE_API_KEY" }
files = { "file": open("audio.mp3", "rb") }
data = { "language": "en" }

response = requests.post(url, headers=headers, files=files, data=data)
print(response.json())`;

  const nodejsCode = `const fs = require('fs');
const FormData = require('form-data');
const fetch = require('node-fetch');

async function speechToText() {
  const form = new FormData();
  form.append('file', fs.createReadStream('audio.mp3'));
  form.append('language', 'en');

  const response = await fetch('https://api.nkenne.ai/v1/speechstt', {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer NKENNE_API_KEY',
      ...form.getHeaders()
    },
    body: form
  });
  const data = await response.json();
  console.log(data);
}

speechToText();`;

  const jsonResponse = `{
    "id": "stt_01HZY28K1B",
    "text": "Welcome to NKENNEai Speech Studio. Your audio has been transcribed successfully.",
    "duration": "4.2",
    "language": "en",
    "tokens": 12
  }`;

  return (
    <div className="space-y-6">
      <div className="flex gap-6 items-start">
        <div className="w-1/2 bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
          <UploadZone
            onFile={setUploadedFile}
            file={uploadedFile}
            onClear={() => { setUploadedFile(null); setTranscript(""); }}
          />
        </div>

        <div className="w-1/2 bg-white border border-gray-200 rounded-2xl p-6 space-y-6 shadow-sm">
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
              SOURCE LANGUAGE
            </p>
            <LanguageDropdown value={language} onChange={setLanguage} />
          </div>

          <div className="flex justify-end pt-2">
            <button
              onClick={handleTranscribe}
              disabled={!uploadedFile || loading}
              className="flex items-center gap-2 bg-[#1e6c56] hover:bg-[#175443] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Transcribe Speech
            </button>
          </div>
        </div>
      </div>

      <div className="flex gap-6 items-start">
        <div className="w-1/2 bg-white border border-gray-200 rounded-2xl p-5 shadow-sm min-h-[150px]">
          <p className="text-sm font-semibold text-gray-800 mb-3 border-b border-gray-100 pb-2">
            Transcript Output
          </p>
          {loading ? (
            <p className="text-sm text-gray-400">Processing transcription...</p>
          ) : transcript ? (
            <p className="text-sm text-gray-800 leading-relaxed">{transcript}</p>
          ) : (
            <p className="text-sm text-gray-300">Transcribe audio to see output here...</p>
          )}
        </div>

        <div className="w-1/2 bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
          <p className="text-sm font-semibold text-gray-800 mb-3 border-b border-gray-100 pb-2">
            Sample response
          </p>
          <pre className="text-xs text-gray-600 font-mono bg-gray-50/50 p-4 rounded-xl overflow-x-auto">
            {jsonResponse}
          </pre>
        </div>
      </div>

      <CodeBlockSection curl={curlCode} python={pythonCode} nodejs={nodejsCode} />
    </div>
  );
};

const SpeechTranslationsTab = () => {
  const [sourceLang, setSourceLang] = useState("English");
  const [targetLang, setTargetLang] = useState("Yoruba");
  const [selectedVoice, setSelectedVoice] = useState("male-1");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const progressRef = useRef(null);

  const handleTranslate = () => {
    if (!uploadedFile || loading) return;
    setLoading(true);
    setAudioReady(false);
    setProgress(0);
    setIsPlaying(false);
    setTimeout(() => {
      setLoading(false);
      setAudioReady(true);
    }, 1800);
  };

  const handlePlayPause = () => {
    if (!isPlaying) {
      setIsPlaying(true);
      let p = progress;
      progressRef.current = setInterval(() => {
        p += 0.01;
        if (p >= 1) {
          p = 1;
          clearInterval(progressRef.current);
          setIsPlaying(false);
        }
        setProgress(p);
      }, 120);
    } else {
      setIsPlaying(false);
      clearInterval(progressRef.current);
    }
  };

  const handleStop = () => {
    setIsPlaying(false);
    setProgress(0);
    clearInterval(progressRef.current);
  };

  const formatTime = (frac) => {
    const total = 12;
    const elapsed = Math.round(frac * total);
    return `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, "0")}`;
  };

  const curlCode = `curl -X POST https://api.nkenne.ai/v1/speechtranslate \\
  -H "Authorization: Bearer NKENNE_API_KEY" \\
  -F "file=@audio.mp3" \\
  -F "source_language=en" \\
  -F "target_language=yo" \\
  -F "voice=male-1"`;

  const pythonCode = `import requests

url = "https://api.nkenne.ai/v1/speechtranslate"
headers = { "Authorization": "Bearer NKENNE_API_KEY" }
files = { "file": open("audio.mp3", "rb") }
data = {
    "source_language": "en",
    "target_language": "yo",
    "voice": "male-1"
}

response = requests.post(url, headers=headers, files=files, data=data)
print(response.json())`;

  const nodejsCode = `const fs = require('fs');
const FormData = require('form-data');
const fetch = require('node-fetch');

async function speechTranslate() {
  const form = new FormData();
  form.append('file', fs.createReadStream('audio.mp3'));
  form.append('source_language', 'en');
  form.append('target_language', 'yo');
  form.append('voice', 'male-1');

  const response = await fetch('https://api.nkenne.ai/v1/speechtranslate', {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer NKENNE_API_KEY',
      ...form.getHeaders()
    },
    body: form
  });
  const data = await response.json();
  console.log(data);
}

speechTranslate();`;

  const jsonResponse = `{
    "id": "str_01HZY28K1C",
    "source_language": "en",
    "target_language": "yo",
    "voice": "yo-male",
    "tokens": 24,
    "url": "https://cdn.nkenne.ai/.../str_01HZY28K1C.mp3"
  }`;

  return (
    <div className="space-y-6">
      <div className="flex gap-6 items-start">
        <div className="w-1/2 bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
          <UploadZone
            onFile={setUploadedFile}
            file={uploadedFile}
            onClear={() => { setUploadedFile(null); setAudioReady(false); }}
          />
        </div>

        <div className="w-1/2 bg-white border border-gray-200 rounded-2xl p-6 space-y-4 shadow-sm">
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
              SOURCE LANGUAGE
            </p>
            <LanguageDropdown value={sourceLang} onChange={setSourceLang} />
          </div>

          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
              TARGET LANGUAGE
            </p>
            <LanguageDropdown value={targetLang} onChange={setTargetLang} />
          </div>

          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
              VOICE
            </p>
            <div className="flex gap-3">
              {VOICES.map((voice) => (
                <VoiceOption
                  key={voice.id}
                  voice={voice}
                  selected={selectedVoice === voice.id}
                  onSelect={setSelectedVoice}
                />
              ))}
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <button
              onClick={handleTranslate}
              disabled={!uploadedFile || loading}
              className="flex items-center gap-2 bg-[#1e6c56] hover:bg-[#175443] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Translate Speech
            </button>
          </div>
        </div>
      </div>

      <div className="flex gap-6 items-start">
        <div className="w-1/2">
          {audioReady ? (
            <AudioPlayer
              title="translation.mp3"
              isPlaying={isPlaying}
              progress={progress}
              onPlayPause={handlePlayPause}
              onStop={handleStop}
              formatTime={formatTime}
            />
          ) : (
            <div className="bg-[#2B6651] rounded-2xl p-6 flex flex-col justify-between min-h-[150px]">
              <div>
                <p className="text-xs font-semibold text-[#7EC8A8] uppercase tracking-widest mb-1">
                  AUDIO OUTPUT
                </p>
                <p className="text-white text-sm font-medium">translation.mp3</p>
              </div>
              <p className="text-white/50 text-sm mt-4">Translate speech to preview audio output</p>
            </div>
          )}
        </div>

        <div className="w-1/2 bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
          <p className="text-sm font-semibold text-gray-800 mb-3 border-b border-gray-100 pb-2">
            Sample response
          </p>
          <pre className="text-xs text-gray-600 font-mono bg-gray-50/50 p-4 rounded-xl overflow-x-auto">
            {jsonResponse}
          </pre>
        </div>
      </div>

      <CodeBlockSection curl={curlCode} python={pythonCode} nodejs={nodejsCode} />
    </div>
  );
};

// ─── PlayGroundTabs Main Component ───────────────────────────────────────────

const PlayGroundTabs = () => {
  // eslint-disable-next-line no-unused-vars
  const [activeTab, setActiveTab] = useState("text-to-text");

  // eslint-disable-next-line no-unused-vars
  const tabs = [
    { id: "text-to-text", label: "Text-to-Text Translation" },
    { id: "text-to-speech", label: "Text-to-Speech Translation" },
    { id: "speech-to-text", label: "Speech-to-Text Translation" },
    { id: "speech-translations", label: "Speech Translations" },
  ];

  return (
    <div className="relative flex flex-col items-center w-full p-[5rem] bg-[#f5faf5] overflow-hidden pt-8">
      {/* Banner */}
      <div className="w-full max-w-5xl mx-auto mb-8 px-4">
        <div className="bg-[#FFF9E6] border border-[#FEF3C7] rounded-lg p-4 flex items-start gap-3">
          <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center mt-0.5">
            <Info className="w-5 h-5 text-black" />
          </div>
          <p className="text-gray-800 text-sm leading-relaxed flex-1">
            You're currently using the free playground powered by slower models.{" "}
            <a
              href="/pricing"
              className="underline text-gray-900 font-medium hover:text-[#1e6c56] transition-colors"
            >
              Subscribe to NKENNEAi
            </a>{" "}
            to access faster, premium translation models.
          </p>
        </div>
      </div>

      {/* Tabs list */}
      {/* <div className="mx-6 flex flex-col md:flex-row items-center justify-around w-full gap-2 mb-8"  >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-5 py-3.5 rounded-2xl text-sm font-semibold transition-all border ${activeTab === tab.id
              ? "bg-[#1e6c56] text-white border-[#1e6c56] shadow-md"
              : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
              }`}
          >
            {tab.label}
          </button>
        ))}
      </div> */}

      {/* Tab panel container */}
      <div className="w-full  px-4">
        {activeTab === "text-to-text" && <ConversionSection />}
        {activeTab === "text-to-speech" && <TextToSpeechTab />}
        {activeTab === "speech-to-text" && <SpeechToTextTab />}
        {activeTab === "speech-translations" && <SpeechTranslationsTab />}
      </div>
      <div className="w-full mt-10">
        <FeatureCardsWelcomeSection />
      </div>
    </div>
  );
};

export default PlayGroundTabs;
